/**
 * DaySpider — easter egg: a spider drops into a calm, empty workday,
 * says hello, spins a web, then potters about it and talks to you.
 *
 * Rendered as a position:absolute overlay that is a SIBLING of the calendar
 * scroller, so its y=0 lines up with the bottom edge of the day header.
 *
 * The overlay is pointerEvents:"none" — permanently. The day cells are live
 * click targets. Hover is detected by hit-testing a window mousemove against
 * the spider/web rects, never by capturing pointer events.
 */
import React, { useEffect, useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";

const first = (s) => {
  const n = String(s || "").trim();
  return n ? n.split(/\s+/)[0] : "";
};

const listNames = (names) => {
  const n = names.filter(Boolean);
  if (n.length === 0) return "";
  if (n.length === 1) return n[0];
  if (n.length === 2) return `${n[0]} and ${n[1]}`;
  return `${n.slice(0, -1).join(", ")} and ${n[n.length - 1]}`;
};

// Lines that reference who is actually in the calendar right now.
// The full team is 4, so `others` is 0–3.
const buildPresenceLines = (rawName, rawOthers) => {
  const me = first(rawName);
  const others = Array.from(
    new Set((rawOthers || []).map(first).filter(Boolean))
  ).filter((n) => n.toLowerCase() !== me.toLowerCase());
  const you = me || "you";
  const n = others.length;

  if (n === 0) {
    return [
      `Just you and me, ${you}.\nEveryone else is actually working.`,
      `Nobody else is here, ${you}.\nI checked. Twice.`,
      `Alone at last, ${you}.\nThe calendar stays empty either way.`,
      `No one to impress, ${you}.\nStill, mind the web.`,
    ];
  }
  if (n === 1) {
    return [
      `${others[0]} is in here too, ${you}.\nTwo witnesses to absolutely nothing.`,
      `You and ${others[0]}, ${you}.\nA whole meeting about an empty day.`,
      `${others[0]} is lurking, ${you}.\nI said nothing. I never do.`,
    ];
  }
  if (n === 2) {
    return [
      `Half the team's here, ${you}:\n${others[0]} and ${others[1]}. Zero assignments.`,
      `${you}, ${others[0]} and ${others[1]}.\nThree of you, one empty day.`,
      `${others[0]} and ${others[1]} are watching too, ${you}.\nI counted. Eight eyes, remember.`,
    ];
  }
  return [
    `Full house, ${you}.\n${others[0]}, ${others[1]} and ${others[2]} — and still nothing.`,
    `Everyone's here: ${listNames(
      others
    )}.\nFour of us guarding an empty day.`,
    `${others[0]}, ${others[1]}, ${others[2]} and you, ${you}.\nThe whole team. For nothing.`,
  ];
};

// Fired the moment a first assignment lands, and mixed into later chatter.
const TAUNTS = [
  "You thought you wouldn't find me after\nadding that assignment? Think again :v",
  "One assignment. Adorable.\nYou thought that would get rid of me?",
  "Oh, you added work. Cute.\nI don't leave over one little assignment.",
  "Nice try. Still here.\nOne assignment changes nothing.",
  "Add one assignment, act like a hero.\nI remain unimpressed and unmoved.",
  "I saw that assignment land.\nI simply chose to ignore it.",
  "One assignment? I've seen scarier things\nin an empty inbox.",
  "You added a task. Congratulations.\nI'm still standing right here.",
  "That assignment doesn't frighten me.\nIt barely interests me.",
];

const SESSION_KEY = (d) => `ce_portal_spider_played_${d}`;

const LINES = {
  intro: "Hey there, looks like a calm day today, huh?",
  firstHover: [
    "If you find any bugs, bring them to me.\nNo need to tell the dev about them.",
    "You found me. There's nothing scheduled.\nI've been guarding it all morning.",
    "Careful. That's a load-bearing web.",
  ],
  revisit: [
    "What are you doing back here?\nThere are still no assignments today, I swear it!",
    "Oh. You again.\nThe day hasn't changed. I have not moved.",
    "You left. I rebuilt three strands.\nNone of it was for you.",
    "Back again? I haven't moved. Not once.",
    "You keep checking. It's still empty. I'd know.",
    "Third time today. I'm flattered. Slightly.",
    "I'm not going anywhere.\nNeither is the emptiness.",
    "Still nothing. I checked while you were gone.",
    "Welcome back. Nothing happened. Again.",
    "You were gone forty seconds. I counted.",
    "The tab stayed open. I saw everything.",
    "Back so soon? The day didn't move either.",
    "You're back. I haven't budged.\nWe make quite a pair.",
    "I kept your spot warm. It's the only spot.",
    "Leaving didn't help. I checked the schedule again.",
    "You and me, checking an empty calendar.\nRepeatedly.",
    "Still empty. I'd tell you if it changed. Probably.",
    "I knew you'd come back. Everyone does.",
  ],
  chatter: [
    "Still here. Still watching. You're welcome.",
    "This is a premium empty day. I curated it myself.",
    "I've been told I have an intimidating web presence.",
    "Eight eyes and I still haven't seen a single bug today.",
    "Quiet day. Just how I like it.\nDon't ruin it.",
    "The structural integrity of this web is not up for debate.",
    "Come back with a bug and we'll talk.",
    "I don't do Mondays.\nOr Tuesdays, apparently.",
    "Somebody has to watch this column. It's me.",
    "I charge rent for this column.\nFirst month is free.",
    "You're hovering. I can tell. I have eight eyes.",
    "This web is load-bearing. Don't touch it.",
    "I've filed today under 'nothing happened'.",
    "The calendar says work day.\nThe calendar lies.",
    "I built this in nine minutes. Nobody noticed.",
    "If anyone asks, I was always here.",
    "Management material, me. I delegate everything.",
    "I spun four hundred metres of silk for this.\nSay thank you.",
    "You blinked. I moved. That's how it works.",
    "Don't tell the dev I'm here.\nThey'll turn me into a feature.",
    "Eight legs, zero meetings. Perfect day.",
    "I'm not lazy. I'm strategically idle.",
    "This day is booked. For nothing. By me.",
    "I counted the empty slots. All of them. Twice.",
    "My review said 'excellent web presence'.",
    "The bug tracker is empty. I took that personally.",
    "I eat bugs. Figuratively.\nPlease don't send tickets.",
    "You could be doing anything.\nAnd yet. A spider.",
    "Here since six. No overtime. Not once.",
    "Nothing scheduled. I've never been busier.",
    "Ask me about my web. Go on. Ask.",
    "I'm the only thing on today's agenda.",
    "This is my column now. I filed the paperwork.",
    "A calm day. Suspiciously calm, if you ask me.",
    "I've seen quieter Tuesdays. Not many.",
    "Forty years of silk work and this is my big break.",
    "I don't take meetings.\nI take fly calls.",
    "Somewhere, a project manager is crying.\nNot my column. Not my problem.",
    "I've reviewed today's schedule.\nIt's a masterpiece.",
    "Do you ever just sit and watch an empty calendar?\nNo? Just me?",
    "I could be anywhere in this app.\nI chose here. Remember that.",
    "Nobody schedules anything without checking with me first.",
    "I'm one hundred percent web, zero percent meetings.",
    "There's a spider on your calendar.\nThat's it. That's the update.",
    "I don't have a bug problem.\nI have a bug buffet.",
    "You came back. The emptiness didn't miss you, but I did. Slightly.",
    "This is the calmest column in the whole portal.\nYou're welcome.",
    "I've been awake since dawn, guarding absolutely nothing.",
    "My to-do list is one line long: 'watch'.",
    "If you need me, I'll be here.\nIf you don't, I'll still be here.",
    "Everyone needs a hobby.\nMine is this column.",
    "I've made peace with the emptiness.\nYou should too.",
    "Statistically, nothing will happen today.\nI ran the numbers.",
    "I don't do small talk. This is medium talk.",
    "I'm not saying I'm the best.\nI'm saying nobody else applied.",
    "Some spiders build webs.\nI built a whole residence.",
    "You're the third person to look at nothing today.",
    "I hope you appreciate the craftsmanship.\nGenuinely. I do.",
    "Careful, I've been known to over-engineer a web.",
    "Quiet, isn't it? That's not luck. That's me.",
    "I've been promoted twice.\nBoth times, in my head.",
    "Nothing on the books. My favourite kind of day.",
    "I would shake your hand, but eight is a lot.",
    "Tell no one about this column.\nIt's ours now.",
    "I work best under zero pressure.\nLucky me.",
    "You've been staring for a while.\nI'm flattered. Mostly.",
    "If the day stays empty, I get to keep the web.",
    "I'm essentially unpaid. And yet, here I am.",
    "This web passed inspection. I inspected it.",
    "Every empty day is a good day.\nEvery good day is a spider day.",
    "You look busy. I look busy. We're both lying.",
    "I have a strict no-fly policy. For flies.",
    "Don't touch the strands.\nYou'll ruin my afternoon.",
    "I've outlived four schedules.\nThis column is next.",
    "Nothing to report.\nReporting anyway.",
  ],
};

// ── Local spider art box (before responsive scale) ───────────────────────────
const BOX_W = 56;
const BOX_H = 56;
const CX = BOX_W / 2; // 28

// Timeline (ms)
const T_DROP = 350;
const T_DROP_DUR = 800;
const T_WAVE = 1650;
const T_WAVE_DUR = 1300;
const T_BUBBLE_IN = 2050;
const T_WEB = 4700;
const T_WALK = 7200;
const BUILD_LOCKOUT = 7200;
const COOLDOWN = 11000;
const MAX_LINES = 12;

const DaySpider = ({
  dayKey,
  dayIndex,
  displayCount,
  containerRef,
  scrollerRef,
  userName,
  otherUsers,
  serviceCount = 0,
  // 2026-11-03 (Amine): when the day holds ONE assignment, the spider must not
  // sit on it or overlap it. `avoidBlock` is that block in CONTENT coordinates
  // ({ topPx, heightPx } — the calendar grid's own 30min = 40px scale), so the
  // spider can pick the half of the visible column the block is NOT in.
  avoidBlock = null,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const overlayRef = useRef(null);
  const [geom, setGeom] = useState(null);

  const [alreadyPlayed] = useState(
    () => Boolean(sessionStorage.getItem(SESSION_KEY(dayKey)))
  );
  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const [pos, setPos] = useState(null); // { x, y } top-left of art box
  const posRef = useRef(null);
  const facingRef = useRef(1);
  const [facing, setFacing] = useState(1);
  const [bubble, setBubble] = useState(null); // { text, key }

  const mountRef = useRef(Date.now());
  const lastLineRef = useRef(0);
  const linesRef = useRef(0);
  const firstSpokenRef = useRef(false);
  const walkTimerRef = useRef(null);
  const bubbleTimerRef = useRef(null);
  const bubbleInTimerRef = useRef(null);
  const hoverRef = useRef(false);

  const lastRepeatRef = useRef(-1);

  // Who's here right now — kept in a ref so live presence changes don't
  // re-render mid-animation, but every new line uses current names.
  const presenceRef = useRef([]);
  presenceRef.current = buildPresenceLines(userName, otherUsers);
  const serviceCountRef = useRef(serviceCount);
  serviceCountRef.current = serviceCount;

  // ── Measure column geometry relative to the calendar container ────────────
  useEffect(() => {
    const measure = () => {
      if (!containerRef?.current || !scrollerRef?.current) return;
      const c = containerRef.current.getBoundingClientRect();
      const s = scrollerRef.current.getBoundingClientRect();

      // 2026-11-03 (Amine): use the scroller's VISIBLE slice, not its full
      // rendered height. `s.height` can be far taller than the screen (the page
      // scrolls), and every position below is a fraction of this value — which
      // is exactly why the spider ended up enormous and parked below the fold.
      // Clamping to the browser viewport keeps the whole overlay on screen.
      const vpTop = Math.max(s.top, 0);
      const vpBottom = Math.min(s.bottom, window.innerHeight);
      const visibleH = Math.max(160, vpBottom - vpTop);

      const timeCol = 90;
      const innerW = scrollerRef.current.clientWidth;
      const colW = (innerW - timeCol) / Math.max(1, displayCount);
      const colLeft = s.left - c.left + timeCol + dayIndex * colW;
      const next = {
        top: vpTop - c.top,
        left: colLeft,
        width: colW,
        height: visibleH,
        containerW: containerRef.current.clientWidth,
      };

      // This now runs on every scroll frame (and the window's), so skip the
      // state write when nothing actually moved — otherwise the component
      // re-renders 60×/s for identical numbers.
      setGeom((prev) => {
        if (!prev) return next;
        const same =
          Math.abs(prev.top - next.top) < 0.5 &&
          Math.abs(prev.left - next.left) < 0.5 &&
          Math.abs(prev.width - next.width) < 0.5 &&
          Math.abs(prev.height - next.height) < 0.5;
        return same ? prev : next;
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef?.current) ro.observe(containerRef.current);
    if (scrollerRef?.current) ro.observe(scrollerRef.current);

    // Re-measure while the page or the grid scrolls. ResizeObserver does not
    // fire for scrolling, so without this the overlay keeps stale coordinates
    // and appears to drift as its viewport slice changes.
    let frame = null;
    const onScroll = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        measure();
      });
    };
    const scrollerEl = scrollerRef.current;
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    scrollerEl?.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      ro.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      scrollerEl?.removeEventListener("scroll", onScroll);
    };
  }, [containerRef, scrollerRef, dayIndex, displayCount]);

  // ── Derived art geometry ──────────────────────────────────────────────────
  const colW = geom?.width || 150;
  const S = Math.max(0.82, Math.min(1.15, colW / 155));
  const SW = BOX_W * S;
  const SH = BOX_H * S;

  const viewH = geom?.height || 400;

  // ── Two fixed anchor spots ────────────────────────────────────────────────
  // 2026-11-03 (Amine): the spider is furniture, it must NOT move when the
  // calendar scrolls. An earlier attempt placed it relative to the assignment's
  // on-screen position (`topPx - scrollTop`), which made it glide down the
  // column with every wheel tick — worse than the overlap it was fixing.
  //
  // So the choice is made in DAY space, which the scroll cannot touch: if the
  // single assignment sits in the upper half of the day grid the spider takes
  // the LOWER spot, and vice versa. Both spots are pure fractions of the
  // visible height, so scrolling never recomputes them.
  //
  // Trade-off, stated plainly: because the spider is pinned and the assignment
  // scrolls, the block can still pass behind the spider when you scroll far
  // enough. Stillness was the explicit requirement; this is the only way to
  // have both a pinned spider and a scrolling grid.
  const maxDropY = Math.max(6, viewH - SH - 56);
  const spotTopY = Math.max(6, Math.min(maxDropY, viewH * 0.08));
  const spotBottomY = Math.max(
    spotTopY + 40,
    Math.min(maxDropY, viewH * 0.62),
  );

  const blockCentrePx = avoidBlock
    ? Number(avoidBlock.topPx || 0) + Number(avoidBlock.heightPx || 0) / 2
    : null;
  const dayMidPx = Number(avoidBlock?.dayMidPx || 0);
  // No assignment, or an unknown day height → keep the spider at the top.
  const blockInUpperDay =
    blockCentrePx === null || dayMidPx <= 0
      ? true
      : blockCentrePx < dayMidPx;

  let dropY = blockInUpperDay ? spotBottomY : spotTopY;

  // Keep enough room under the spider for the web to be worth drawing; if the
  // chosen spot leaves too little, use the other one. Still static — this only
  // depends on the visible height.
  if (viewH - (dropY + SH) < 96) {
    const other = blockInUpperDay ? spotTopY : spotBottomY;
    if (viewH - (other + SH) >= 96) dropY = other;
  }

  const threadX = colW / 2;

  // Web grows taller as the day goes on. Ticks once a minute.
  const [clockTick, setClockTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setClockTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const _d = new Date(clockTick);
  const minsOfDay = _d.getHours() * 60 + _d.getMinutes();
  const DAY_FROM = 7 * 60;
  const DAY_TO = 19 * 60;
  const dayProgress = Math.min(
    1,
    Math.max(0.12, (minsOfDay - DAY_FROM) / (DAY_TO - DAY_FROM))
  );

  const availTop = dropY + SH + 14;
  const availH = Math.max(80, viewH - availTop - 14);

  // Web fills the FULL width of the day column and runs as tall as the space
  // allows. Not a circle — a tall rectangle of web.
  const webRX = Math.max(30, colW / 2 - 2);
  const maxRY = Math.max(40, availH / 2);
  // Grows taller through the day: half height in the morning, full by evening
  const webRY = Math.max(36, maxRY * (0.5 + 0.5 * dayProgress));
  const webCX = threadX;
  const webCY = availTop + webRY;

  const startPos = { x: threadX - SW / 2, y: dropY };

  // Walk bounds — inside the web, clamped to the column and to a visible band
  const walkMinX = Math.max(2, webCX - webRX * 0.72 - SW / 2);
  const walkMaxX = Math.min(colW - SW - 2, webCX + webRX * 0.72 - SW / 2);
  const walkBand = Math.min(webRY * 0.55, 78);
  const walkMinY = Math.max(dropY + 4, webCY - walkBand - SH / 2);
  const walkMaxY = Math.min(
    viewH - SH - 4,
    webCY + walkBand - SH / 2
  );

  // ── Web strands (8 spokes + 3 rings) ──────────────────────────────────────
  const strands = React.useMemo(() => {
    const out = [];
    const spokes = 8;
    for (let i = 0; i < spokes; i += 1) {
      const a = (i * Math.PI * 2) / spokes - Math.PI / 2;
      const x = webCX + Math.cos(a) * webRX;
      const y = webCY + Math.sin(a) * webRY;
      out.push({
        d: `M${webCX},${webCY} L${x},${y}`,
        delay: i * 60,
        w: 1,
      });
    }
    [0.36, 0.66, 0.95].forEach((f, ri) => {
      const pts = [];
      for (let i = 0; i < spokes; i += 1) {
        const a = (i * Math.PI * 2) / spokes - Math.PI / 2;
        pts.push(
          `${(webCX + Math.cos(a) * webRX * f).toFixed(1)},${(
            webCY +
            Math.sin(a) * webRY * f
          ).toFixed(1)}`
        );
      }
      out.push({
        d: `M${pts.join(" L")} Z`,
        delay: 480 + ri * 300,
        w: 1,
      });
    });
    return out;
  }, [webCX, webCY, webRX, webRY]);

  const webOpacity = 0.42;
  const stroke = isDark ? "#A8C0D0" : "#5A6770";
  const accent = theme.palette.primary.main;

  // ── Set the spider down after the drop ────────────────────────────────────
  // Depends on the MEASURED numbers, not the `geom` object identity: `geom` is
  // rebuilt on every scroll re-measure, and keying on the object would snap the
  // spider back to its start position mid-walk on every wheel tick.
  const geomTop = geom?.top;
  const geomLeft = geom?.left;
  const geomW = geom?.width;
  const geomH = geom?.height;
  useEffect(() => {
    if (!geom) return undefined;
    setPos(startPos);
    posRef.current = startPos;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geomTop, geomLeft, geomW, geomH]);

  // ── Walk ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!geom || reducedMotion) return undefined;

    const schedule = () => {
      const pause = 900 + Math.random() * 1500;
      walkTimerRef.current = setTimeout(() => {
        if (hoverRef.current) {
          schedule();
          return;
        }
        if (walkMaxX <= walkMinX || walkMaxY <= walkMinY) {
          schedule();
          return;
        }
        const nx = walkMinX + Math.random() * (walkMaxX - walkMinX);
        const ny = walkMinY + Math.random() * (walkMaxY - walkMinY);
        const dx = nx - (posRef.current?.x ?? startPos.x);
        posRef.current = { x: nx, y: ny };
        setPos({ x: nx, y: ny });
        if (Math.abs(dx) > 4) {
          facingRef.current = dx >= 0 ? 1 : -1;
          setFacing(facingRef.current);
        }
        walkTimerRef.current = setTimeout(schedule, 750);
      }, pause);
    };

    walkTimerRef.current = setTimeout(schedule, T_WALK);
    return () => clearTimeout(walkTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geom, reducedMotion]);

  // ── Dialogue ──────────────────────────────────────────────────────────────
  const showLine = React.useCallback(
    (text) => {
      lastLineRef.current = Date.now();
      clearTimeout(bubbleTimerRef.current);
      clearTimeout(bubbleInTimerRef.current);
      setBubble({ text, key: Date.now() });
      const flat = text.replace(/\n/g, " ");
      const dur =
        flat.length <= 45 ? 6000 : flat.length <= 70 ? 7500 : 9000;
      bubbleTimerRef.current = setTimeout(() => setBubble(null), dur);
    },
    []
  );

  // Intro / revisit bubble
  useEffect(() => {
    if (!geom) return undefined;
    const isRevisit = alreadyPlayed;
    if (!isRevisit) {
      sessionStorage.setItem(SESSION_KEY(dayKey), "1");
    }
    const delay = isRevisit ? 1200 : T_BUBBLE_IN;
    const t = setTimeout(() => {
      const text = isRevisit
        ? (() => {
            const rp = [...presenceRef.current, ...LINES.revisit];
            let i = Math.floor(Math.random() * rp.length);
            if (rp.length > 1 && i === lastRepeatRef.current) {
              i = (i + 1) % rp.length;
            }
            lastRepeatRef.current = i;
            return rp[i];
          })()
        : LINES.intro;
      setBubble({ text, key: Date.now() });
      clearTimeout(bubbleTimerRef.current);
      bubbleTimerRef.current = setTimeout(
        () => setBubble(null),
        isRevisit ? 8500 : 6500
      );
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geom]);

  // ── Gloat the moment a first assignment lands ─────────────────────────────
  const prevCountRef = useRef(serviceCount);
  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = serviceCount;
    if (!(prev === 0 && serviceCount === 1)) return undefined;
    const t = setTimeout(() => {
      showLine(TAUNTS[Math.floor(Math.random() * TAUNTS.length)]);
    }, 900);
    return () => clearTimeout(t);
  }, [serviceCount, showLine]);

  const onHoverHit = React.useCallback(() => {
    const now = Date.now();
    if (now - mountRef.current < BUILD_LOCKOUT) return;
    if (now - lastLineRef.current < COOLDOWN) return;
    if (linesRef.current >= MAX_LINES) return;
    linesRef.current += 1;

    if (!firstSpokenRef.current) {
      firstSpokenRef.current = true;
      const fp = LINES.firstHover;
      showLine(fp[Math.floor(Math.random() * fp.length)]);
      return;
    }
    const pool = [
      ...presenceRef.current,
      ...(serviceCountRef.current === 1 ? TAUNTS : []),
      ...LINES.chatter,
    ];
    let idx = Math.floor(Math.random() * pool.length);
    if (pool.length > 1 && idx === lastRepeatRef.current) {
      idx = (idx + 1 + Math.floor(Math.random() * (pool.length - 1))) % pool.length;
    }
    lastRepeatRef.current = idx;
    showLine(pool[idx]);
  }, [showLine]);

  // ── Hover hit-test (window mousemove, overlay stays pointer-events:none) ──
  useEffect(() => {
    if (!geom) return undefined;
    let raf = 0;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const el = overlayRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const px = e.clientX - r.left;
        const py = e.clientY - r.top;
        const p = posRef.current || startPos;
        const pad = 12;
        const onSpider =
          px >= p.x - pad &&
          px <= p.x + SW + pad &&
          py >= p.y - pad &&
          py <= p.y + SH + pad;
        const onWeb =
          px >= webCX - webRX - 6 &&
          px <= webCX + webRX + 6 &&
          py >= webCY - webRY - 6 &&
          py <= webCY + webRY + 6;
        const hit = onSpider || onWeb;
        hoverRef.current = hit;
        if (hit) onHoverHit();
      });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geom, onHoverHit, webCX, webCY, webRX, webRY, SW, SH]);

  useEffect(
    () => () => {
      clearTimeout(walkTimerRef.current);
      clearTimeout(bubbleTimerRef.current);
      clearTimeout(bubbleInTimerRef.current);
    },
    []
  );

  if (!geom || !pos) return null;

  // ── Bubble placement (clamped to the calendar container) ──────────────────
  const BUBBLE_W = Math.min(200, Math.max(160, colW + 30));
  const rawLeft = pos.x + SW / 2 - BUBBLE_W + 24;
  const absMin = 6 - geom.left;
  const absMax = (geom.containerW || colW) - 6 - BUBBLE_W - geom.left;
  const bubbleLeft = Math.min(
    Math.max(rawLeft, absMin),
    Math.max(absMin, absMax)
  );
  const tailLeft = Math.max(
    12,
    Math.min(BUBBLE_W - 24, pos.x + SW / 2 - bubbleLeft - 6)
  );
  const bubbleTop = pos.y - 92;

  const bubbleBg = isDark ? "rgba(24,29,35,0.97)" : "rgba(255,255,255,0.98)";

  return (
    <Box
      ref={overlayRef}
      aria-hidden="true"
      sx={{
        position: "absolute",
        top: `${geom.top}px`,
        left: `${geom.left}px`,
        width: `${colW}px`,
        height: `${geom.height}px`,
        pointerEvents: "none",
        zIndex: 20,
        overflow: "visible",
        "@media print": { display: "none" },
      }}
    >
      <Box
        component="svg"
        sx={{ overflow: "visible", position: "absolute", top: 0, left: 0 }}
        width={colW}
        height={geom.height}
        viewBox={`0 0 ${colW} ${geom.height}`}
      >
        {/* ── Orb web ── */}
        <g opacity={webOpacity}>
          {strands.map((s, i) => (
            <path
              key={i}
              d={s.d}
              fill="none"
              stroke={stroke}
              strokeWidth={s.w}
              strokeLinecap="round"
              style={
                reducedMotion || alreadyPlayed
                  ? undefined
                  : {
                      strokeDasharray: 420,
                      strokeDashoffset: 420,
                      animation: `spiderStrand 420ms ease-in ${
                        T_WEB + s.delay
                      }ms forwards`,
                    }
              }
            />
          ))}
        </g>

        {/* ── Thread — anchored at the drop point, fades once the spider walks ── */}
        {!alreadyPlayed && (
          <line
            x1={startPos.x + SW / 2}
            y1={0}
            x2={startPos.x + SW / 2}
            y2={startPos.y + 12 * S}
            stroke={accent}
            strokeWidth={1}
            opacity={0.85}
            style={
              reducedMotion
                ? undefined
                : {
                    "--tlen": `${startPos.y + 12 * S}`,
                    strokeDasharray: startPos.y + 12 * S,
                    strokeDashoffset: startPos.y + 12 * S,
                    animation: `spiderThread ${T_DROP_DUR}ms cubic-bezier(.25,.1,.55,1) ${T_DROP}ms forwards, spiderThreadFade 700ms ease ${T_WALK}ms forwards`,
                  }
            }
          />
        )}

        {/* ── Spider ── */}
        <g
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px)`,
            transition: reducedMotion
              ? undefined
              : "transform 780ms cubic-bezier(.4,0,.6,1)",
          }}
        >
          {/* Flip is instant — never transitioned, or scaleX passes through 0 */}
          <g
            style={{
              transform: `scaleX(${facing})`,
              transformOrigin: `${SW / 2}px ${SH / 2}px`,
              transformBox: "view-box",
            }}
          >
            <g
              style={
                reducedMotion || alreadyPlayed
                  ? {}
                  : {
                      animation: `spiderDrop ${T_DROP_DUR}ms cubic-bezier(.25,.1,.55,1) ${T_DROP}ms both`,
                    }
              }
            >
              <g style={{ transform: `scale(${S})`, transformOrigin: "0 0" }}>
              {/* Legs — all 8 root on the ABDOMEN, none on the head */}
              <g
                fill="none"
                stroke={isDark ? "#DCE6EE" : "#3B444B"}
                strokeWidth={2.1}
                strokeLinecap="round"
              >
                {/* Waving leg — root tucked inside the abdomen */}
                <g transform="translate(19,23)">
                  <path
                    d="M0,0 Q-11,-9 -18,-17"
                    style={{
                      transformBox: "fill-box",
                      transformOrigin: "100% 100%",
                      ...(reducedMotion || alreadyPlayed
                        ? {}
                        : {
                            animation: `spiderWave ${T_WAVE_DUR}ms ease-in-out ${T_WAVE}ms 2`,
                          }),
                    }}
                  />
                </g>
                <path d="M18,28 Q7,25 1,28" />
                <path d="M18,34 Q7,38 2,43" />
                <path d="M20,40 Q11,48 7,53" />
                <path d="M37,23 Q48,14 55,6" />
                <path d="M38,28 Q49,25 55,28" />
                <path d="M38,34 Q49,38 54,43" />
                <path d="M36,40 Q45,48 49,53" />
              </g>

              {/* Abdomen — rounded rectangle, carries every leg */}
              <rect x={CX - 11} y={21} width={22} height={26} rx={7} ry={7} fill={stroke} />
              {/* Head — rounded square, sits forward of the abdomen */}
              <rect x={CX - 6.5} y={5.5} width={13} height={13} rx={3.5} ry={3.5} fill={stroke} />

              {/* Eyes */}
              <circle cx={CX - 2.6} cy={11.4} r={1.9} fill={isDark ? "#101317" : "#F7FAFC"} />
              <circle cx={CX + 2.6} cy={11.4} r={1.9} fill={isDark ? "#101317" : "#F7FAFC"} />
              <circle cx={CX - 3.2} cy={10.7} r={0.8} fill={isDark ? "#9FB3C4" : "#7C8B96"} />
              <circle cx={CX + 2} cy={10.7} r={0.8} fill={isDark ? "#9FB3C4" : "#7C8B96"} />

              {/* Smile */}
              <path
                d="M25.4,14.6 Q28,16.8 30.6,14.6"
                fill="none"
                stroke={isDark ? "#101317" : "#F7FAFC"}
                strokeWidth={1.1}
                strokeLinecap="round"
                opacity={0.9}
              />
              </g>
            </g>
          </g>
        </g>

        <defs>
          <style>{`
            @keyframes spiderStrand {
              from { stroke-dashoffset: 420; }
              to   { stroke-dashoffset: 0; }
            }
            @keyframes spiderThread {
              from { stroke-dashoffset: var(--tlen); }
              to   { stroke-dashoffset: 0; }
            }
            @keyframes spiderThreadFade {
              from { opacity: 0.85; }
              to   { opacity: 0; }
            }
            @keyframes spiderDrop {
              0%   { transform: translate(0px, ${-SH - 30}px); opacity: 0; }
              12%  { opacity: 1; }
              72%  { transform: translate(0px, 7px); opacity: 1; }
              100% { transform: translate(0px, 0px); opacity: 1; }
            }
            @keyframes spiderWave {
              0%   { transform: rotate(0deg); }
              14%  { transform: rotate(-78deg); }
              34%  { transform: rotate(-38deg); }
              54%  { transform: rotate(-78deg); }
              74%  { transform: rotate(-38deg); }
              100% { transform: rotate(0deg); }
            }
          `}</style>
        </defs>
      </Box>

      {/* ── Speech bubble ── */}
      {bubble && (
        <Box
          key={bubble.key}
          sx={{
            position: "absolute",
            left: `${bubbleLeft}px`,
            top: `${Math.max(2, bubbleTop)}px`,
            width: `${BUBBLE_W}px`,
            background: bubbleBg,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "10px",
            px: "9px",
            py: "7px",
            boxSizing: "border-box",
            zIndex: 2,
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-7px",
              left: `${tailLeft}px`,
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: `7px solid ${theme.palette.divider}`,
            },
            "&::before": {
              content: '""',
              position: "absolute",
              bottom: "-5.5px",
              left: `${tailLeft + 1}px`,
              width: 0,
              height: 0,
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderTop: `6px solid ${bubbleBg}`,
              zIndex: 1,
            },
          }}
        >
          <Box
            component="span"
            sx={{
              display: "block",
              whiteSpace: "pre-wrap",
              fontFamily: "'Work Sans', sans-serif",
              fontSize: "11px",
              fontWeight: 500,
              lineHeight: 1.45,
              color: theme.palette.text.primary,
            }}
          >
            {bubble.text}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DaySpider;
